import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { User, Building, Settings, ArrowRight, ArrowLeft } from 'lucide-react';

interface OnboardingData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  workspaceId: string;
  role: string;
  preferences: {
    notifications: {
      email: boolean;
      sms: boolean;
      slack: boolean;
      inApp: boolean;
    };
  };
}

interface EngineeringOnboardingProps {
  onComplete: (userData: OnboardingData) => void;
  onSkip?: () => void;
  mode: 'demo' | 'live';
}

export const EngineeringOnboarding: React.FC<EngineeringOnboardingProps> = ({
  onComplete,
  onSkip,
  mode
}) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<OnboardingData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    workspaceId: mode === 'demo' ? 'demo-workspace' : '',
    role: 'user',
    preferences: {
      notifications: {
        email: true,
        sms: false,
        slack: true,
        inApp: true
      }
    }
  });

  const steps = [
    { title: 'Personal Information', icon: User },
    { title: 'Workspace Setup', icon: Building },
    { title: 'Preferences', icon: Settings }
  ];

  const handleNext = () => {
    if (step < steps.length) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      await onComplete(formData);
    } catch (error) {
      console.error('Onboarding failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.firstName && formData.lastName && formData.email && (mode === 'demo' || formData.password);
      case 2:
        return formData.workspaceId && formData.role;
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-2xl"
      >
        <Card className="sphix-card-enhanced">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">
              Welcome to Engineering Bridge
            </CardTitle>
            <p className="text-slate-400 mt-2">
              {mode === 'demo' ? 'Set up your demo profile' : 'Create your account and get started'}
            </p>
            <div className="flex justify-center mt-6">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    i + 1 <= step ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-400'
                  }`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`w-16 h-1 mx-2 transition-colors ${
                      i + 1 < step ? 'bg-purple-600' : 'bg-slate-700'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-4">
              <span className="text-sm text-slate-400">
                Step {step} of {steps.length}: {steps[step - 1].title}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -20, opacity: 0 }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">First Name *</Label>
                      <Input
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                         className="bg-slate-800 border-slate-600 text-black"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Last Name *</Label>
                      <Input
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                         className="bg-slate-800 border-slate-600 text-black"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-black"
                      placeholder="john.doe@company.com"
                    />
                  </div>
                  {mode === 'live' && (
                    <div>
                      <Label className="text-slate-300">Password *</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                         className="bg-slate-800 border-slate-600 text-black"
                        placeholder="Create a secure password"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-300">Phone Number</Label>
                    <Input
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-black"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -20, opacity: 0 }}
                  className="space-y-4"
                >
                  <div>
                    <Label className="text-slate-300">Workspace ID *</Label>
                    <Input
                      value={formData.workspaceId}
                      onChange={(e) => setFormData({...formData, workspaceId: e.target.value})}
                      className="bg-slate-800 border-slate-600 text-black"
                      placeholder={mode === 'demo' ? 'demo-workspace' : 'your-company-workspace'}
                      disabled={mode === 'demo'}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {mode === 'demo' ? 'Demo workspace is pre-configured' : 'This identifies your organization'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Role *</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                      <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="developer">Developer</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -20, opacity: 0 }}
                  className="space-y-4"
                >
                  <div>
                    <Label className="text-slate-300 text-base">Notification Preferences</Label>
                    <p className="text-sm text-slate-500 mb-4">Choose how you'd like to receive updates</p>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="email"
                          checked={formData.preferences.notifications.email}
                          onChange={(e) => 
                            setFormData({
                              ...formData,
                              preferences: {
                                ...formData.preferences,
                                notifications: {
                                  ...formData.preferences.notifications,
                                  email: e.target.checked
                                }
                              }
                            })
                          }
                        />
                        <Label htmlFor="email" className="text-slate-300">Email notifications</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="sms"
                          checked={formData.preferences.notifications.sms}
                          onChange={(e) => 
                            setFormData({
                              ...formData,
                              preferences: {
                                ...formData.preferences,
                                notifications: {
                                  ...formData.preferences.notifications,
                                  sms: e.target.checked
                                }
                              }
                            })
                          }
                        />
                        <Label htmlFor="sms" className="text-slate-300">SMS notifications</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="slack"
                          checked={formData.preferences.notifications.slack}
                          onChange={(e) => 
                            setFormData({
                              ...formData,
                              preferences: {
                                ...formData.preferences,
                                notifications: {
                                  ...formData.preferences.notifications,
                                  slack: e.target.checked
                                }
                              }
                            })
                          }
                        />
                        <Label htmlFor="slack" className="text-slate-300">Slack notifications</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="inApp"
                          checked={formData.preferences.notifications.inApp}
                          onChange={(e) => 
                            setFormData({
                              ...formData,
                              preferences: {
                                ...formData.preferences,
                                notifications: {
                                  ...formData.preferences.notifications,
                                  inApp: e.target.checked
                                }
                              }
                            })
                          }
                        />
                        <Label htmlFor="inApp" className="text-slate-300">In-app notifications</Label>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="flex justify-between pt-4">
              {onSkip && step === 1 && (
                <Button variant="ghost" onClick={onSkip} className="text-slate-400 hover:text-slate-300">
                  Skip for now
                </Button>
              )}
              <div className="flex gap-3 ml-auto">
                {step > 1 && (
                  <Button variant="outline" onClick={handleBack} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                <Button 
                  onClick={handleNext} 
                  disabled={!isStepValid() || isLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isLoading ? (
                    'Setting up...'
                  ) : step === steps.length ? (
                    'Complete Setup'
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
